import {
  type BreadcrumbDataDTO,
  type FacetDTO,
  type FacetValueDTO,
  type SearchProductDataDTO,
  type SearchResultsDTO,
  type SortingOptionDTO,
  type SRPTreeViewNodeDTO,
} from "../models/SearchResultsDTO";
import {
  type BreadcrumbData,
  type FacetData,
  type FacetValueData,
  type FeatureData,
  type SearchResultData,
  type SearchResultRow,
  type SrpProductData,
} from "~/utils/models";
import { BreadcrumbType } from "../../../utils/models";
import { splitSortingCode } from "~/common/utils/misc";
import { type ContentSearchResultsDTO } from "../api/fetchContentSearchResults";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import uniqBy from "lodash/uniqBy";
import { getNextUID } from "~/common/utils/uid";
import { SortDirection, type SortOption, type SRPSeriesTreeNode } from "../models/models";
import { type ProductDataDTO } from "~/domains/product/types/ProductDataDTO";
import { transformSrpProduct } from "~/features/search/transformers/transformSrpProduct";
import { makeCADUrl, makeCalcUrl } from "~/utils/productUtils";

import { isNonNull } from "~/common/utils/reducers";
import { transformFilterConfig } from "~/features/search/api/fetchFilterConfig";
import { getCategoryVisibilityFunction, transformCategoryPaths } from "~/features/search/api/fetchProductGroups";
import type { FilterConfig, TaxonomyFilterData } from "~/features/search/models/filterConfigModels";
import { type SearchPageType } from "~/features/search/models/SearchPageType";

function transformProducts(dtos: SearchProductDataDTO[]): SrpProductData[] {
  let products = dtos.flatMap((item) => {
    const product = transformSrpProduct(item);
    const variantsProducts: SrpProductData[] = (item.variantProducts ?? [])
      .map((x) => transformProduct(x))
      .map((variantProduct) => ({
        ...variantProduct,
        cadUrl: makeCADUrl(variantProduct),
        calcUrl: product.calcUrl ? makeCalcUrl(variantProduct) : undefined,
      }));
    return [product, ...variantsProducts];
  });
  products = uniqBy(products, (item) => item.code);
  return products;
}

export function transformSearchResults(dto: SearchResultsDTO, pageType?: SearchPageType): SearchResultData {
  const allFacets = (dto.facets || []).concat(dto.otherFacets || []);

  const results = transformProducts(dto.results);

  const isCategoryVisible = getCategoryVisibilityFunction(
    allFacets.find((facet) => facet.code === "categoryPath"),
    pageType,
  );

  const fullTaxonomy = transformCategoryPaths(dto.categoryPaths ?? [], isCategoryVisible);

  let filterConfig: FilterConfig = [];
  if (dto.facetConfig?.solrFacetConfigData) {
    filterConfig = transformFilterConfig(dto.facetConfig.solrFacetConfigData.groups, pageType);
    const metricCategoryFilter = filterConfig.find((filter) => filter.type === "TAXONOMY") as
      | TaxonomyFilterData
      | undefined;
    if (metricCategoryFilter) {
      metricCategoryFilter.categories = fullTaxonomy;
    }
  }

  let filterConfigImperial: FilterConfig = [];
  if (dto.facetConfig?.solrFacetConfigDataImperial) {
    filterConfigImperial = transformFilterConfig(dto.facetConfig.solrFacetConfigDataImperial.groups, pageType);
    const imperialCategoryFilter = filterConfigImperial.find((filter) => filter.type === "TAXONOMY") as
      | TaxonomyFilterData
      | undefined;
    if (imperialCategoryFilter) {
      imperialCategoryFilter.categories = fullTaxonomy;
    }
  }

  const tabularViewColConfig = dto.facetConfig?.featureList ?? [];
  const tabularViewColConfigImperial = dto.facetConfig?.featureListImperial ?? [];

  const featureCodeToName: Record<string, string> = {};
  [...tabularViewColConfig, ...tabularViewColConfigImperial].forEach((filter) => {
    featureCodeToName[filter.code] = filter.name;
  });

  return {
    results: dto.results.map(transformRow(featureCodeToName)),
    sorts: transformSorts(dto.sorts || []),
    pagination: dto.pagination,
    products: uniqBy(results, (item) => item.code),
    facets: allFacets.map(transformFacet),
    selectedSort: getSelectedSort(dto.pagination.sort),
    content: transformContentSearchResults(dto.contentData),
    seriesTree: dto.treeView ? dto.treeView.map((node) => transformSeriesTreeNode(node, null)).filter(isNonNull) : null,
    hasTubularResults: dto.hasTubularResults,
    technicalInfo: dto.technicalInfo ?? [],
    hasCampaignResults: dto.hasCampaignResults ?? false,

    filterConfig,
    filterConfigImperial,
    tabularViewColConfig,
    tabularViewColConfigImperial,

    fullTaxonomy,
    breadcrumbs: dto.breadcrumbs.map(transformBreadcrumb),
  };
}

function transformBreadcrumb(dto: BreadcrumbDataDTO): BreadcrumbData {
  if (dto.isTechDimensionFilterFacet) {
    return {
      type: BreadcrumbType.TECH_VALUE,
      value: Number.parseFloat(dto.facetValueCode),
      valueType: "MISSING",
      uom: "MISSING",
      code: dto.facetCode,
      name: dto.facetName,
    };
  }
  return {
    type: BreadcrumbType.FACET,
    facetValueCode: dto.facetValueCode,
    facetCode: dto.facetCode,
    name: dto.facetName,
    value: dto.facetValueName,
  };
}

export function transformSeriesTreeNode(
  dto: SRPTreeViewNodeDTO,
  parent: SRPTreeViewNodeDTO | null,
): SRPSeriesTreeNode | null {
  if (dto.code?.toUpperCase() === "NOSERIES" || dto.name?.toUpperCase() === "NOSERIES") {
    // Remove fake categories created by BE because they are not needed and BE can't bother fixing it
    return null;
  }
  const isSeries = dto.isSeries ?? false;
  return {
    id: dto.code,
    name: dto.name,
    categoryCode: adjustSeriesTreeCategoryCode(dto.code, isSeries),
    parentCategoryCode: parent ? adjustSeriesTreeCategoryCode(parent.code, parent.isSeries) : "",
    minDiameter: isSeries ? dto.minDiameter : undefined,
    maxDiameter: isSeries ? dto.maxDiameter : undefined,
    minImperialDiameter: isSeries ? dto.minImperialDiameter : undefined,
    maxImperialDiameter: isSeries ? dto.maxImperialDiameter : undefined,
    isImperialConverted: isSeries ? dto.isImperialConverted : undefined,
    count: dto.count ?? 0,
    expanded: false, // todo: logic to have the currently selected category and all it's parent expanded
    children: dto.children.map((node) => transformSeriesTreeNode(node, dto)).filter(isNonNull),
    isSeries,
  };
}

function adjustSeriesTreeCategoryCode(code: string, isSeries?: boolean | null) {
  if (!isSeries) {
    return code;
  }
  const parts = code.split("/");
  parts[parts.length - 1] = "*_" + parts[parts.length - 1];
  return parts.join("/");
}

function transformRow(featureCodeToName: Record<string, string>) {
  return (dto: SearchProductDataDTO): SearchResultRow => {
    return {
      id: getNextUID(),
      productCode: dto.code,
      variantProductCodes: dto.variantProducts?.map((product) => product.code) ?? [],
      features: transformFeatures(dto, featureCodeToName),
    };
  };
}

export function transformContentSearchResults(dto: ContentSearchResultsDTO | null) {
  return {
    results: dto?.contentData ?? [],
    pagination: dto?.pagination ?? {
      currentPage: 1,
      numberOfPages: 0,
      pageSize: 0,
      totalNumberOfResults: 0,
    },
  };
}

function getSelectedSort(sortCode: string) {
  if (sortCode) {
    const sortData = splitSortingCode(sortCode);
    return {
      code: sortData.code,
      direction: sortData.sortingDirection,
    };
  }
  return {
    code: "",
    direction: null,
  };
}

export function transformSorts(dto: SortingOptionDTO[]): SortOption[] {
  const codes: SortOption[] = [];
  const codesMap: { [key: string]: boolean } = {};
  const allCodes = dto.map((item) => splitSortingCode(item.code));

  for (const item of allCodes) {
    const showSortingArrow =
      allCodes.some((code) => code.code === item.code && code.sortingDirection === SortDirection.ASC) &&
      allCodes.some((code) => code.code === item.code && code.sortingDirection === SortDirection.DESC);
    if (!codesMap[item.code]) {
      const name = dto.find((option) => option.code.includes(item.code))?.name;
      const isAscdesc = item.sortingDirection !== null;
      codesMap[item.code] = true;
      codes.push({
        code: item.code,
        isAscdesc,
        sortingDirection: showSortingArrow ? SortDirection.ASC : isAscdesc ? item.sortingDirection : undefined,
        showSortingArrow,
        name: name,
      } as SortOption);
    }
  }
  return codes;
}

function transformFacet(dto: FacetDTO): FacetData {
  const dtoValues = !dto.values ? [] : dto.values;
  return {
    code: dto.code,
    name: dto.name,
    values: dtoValues.map(transformFacetValue),
  };
}

function transformFacetValue(dto: FacetValueDTO): FacetValueData {
  return {
    code: dto.code,
    name: dto.name,
    count: dto.count,
    selected: dto.selected || false,
  };
}

export const transformFeatures = (dto: ProductDataDTO, featureCodeToName: Record<string, string>): FeatureData[] => {
  const features: FeatureData[] = [];

  if (dto.classifications) {
    for (const classification of dto.classifications) {
      if (classification.features) {
        for (const feature of classification.features) {
          features.push({
            name: feature.name ?? featureCodeToName[feature.techFilterId],
            abbreviation:
              feature.abbreviation + (feature.abbreviationAddition ? `<sub>${feature.abbreviationAddition}</sub>` : ""),
            unit: feature.featureUnit ? feature.featureUnit.symbol : "",
            values: feature.featureValues?.map((v) => v.value) ?? [],
            techFilterId: feature.techFilterId,
            technicalDescription: feature.technicalDescription,
            type: feature.type,
            isConverted: feature.isConverted,
          });
        }
      }
    }
  }
  return features;
};
